/**
 * FilterSheet — the shared mobile pattern for secondary filters.
 *
 * Phase 6: on a 390px viewport a permanent stack of selects eats half the
 * screen before the first row of data. The search stays inline (it is the
 * primary way in); everything else moves behind one trigger that states how
 * many filters are active. Same filter components, same handlers: only the
 * container changes.
 */
import { useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from "@/components/ui/sheet";

export function FilterSheet({
  children,
  activeCount = 0,
  onReset,
  resetLabel = "Reset filtri",
  title = "Filtri",
  description = "Restringi l'elenco. Le scelte si applicano subito.",
  testId = "filter-sheet",
}) {
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          data-testid={`${testId}-trigger`}
          className="h-10 border-line text-content-secondary hover:bg-surface-subtle"
        >
          <SlidersHorizontal className="w-4 h-4 mr-2" aria-hidden="true" />
          {title}
          {activeCount > 0 && (
            <Badge
              data-testid={`${testId}-active-count`}
              className="ml-2 bg-brand/20 text-brand-accent text-label"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        data-testid={testId}
        className="bg-surface border-line max-h-[85vh] overflow-y-auto rounded-t-card
                   pb-[calc(1rem+env(safe-area-inset-bottom))]
                   [&>button]:w-10 [&>button]:h-10 [&>button]:flex [&>button]:items-center [&>button]:justify-center"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="text-content">{title}</SheetTitle>
          <SheetDescription className="text-content-muted">{description}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">{children}</div>
        <div className="mt-6 flex items-center gap-2">
          {onReset && (
            <Button
              variant="outline"
              data-testid={`${testId}-reset`}
              onClick={onReset}
              disabled={activeCount === 0}
              className="h-10 border-line text-content-secondary disabled:opacity-40"
            >
              {resetLabel}
            </Button>
          )}
          <Button
            data-testid={`${testId}-apply`}
            onClick={() => setOpen(false)}
            className="h-10 flex-1 bg-brand hover:bg-brand-hover text-content-on-brand"
          >
            Mostra risultati
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default FilterSheet;
